import { z } from 'zod';
import { trackingSchema } from './tracking-schema';

/** The contributor form posts here (React form and its plain twin). */
export const CONTRIBUTE_FORM_ENDPOINT = '/api/forms/contribute';

/** The form's key through the delivery pipeline. */
export const CONTRIBUTE_FORM_KEY = 'contribute';

/**
 * What someone can offer the blog, in the order the dropdown shows them. The
 * label is the stored value, like the eBook's event-timing answers, so the
 * notification reads without a lookup.
 */
export const COLLABORATION_TYPES = [
  'Write an article for the blog',
  'Offer an expert quote or interview',
  'Suggest a study or resource we could cite',
  'Co-marketing or event partnership',
  'Something else',
] as const;

export type CollaborationType = (typeof COLLABORATION_TYPES)[number];

/**
 * Control names shared by the contributor form and its plain HTML twin, so
 * both post the keys this schema reads. The React form also uses them as
 * element ids; there is one form on the page.
 */
export const CONTRIBUTE_FIELDS = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  company: 'company',
  siteUrl: 'siteUrl',
  collaborationType: 'collaborationType',
  topic: 'topic',
  pitch: 'pitch',
  samples: 'samples',
} as const;

/** Shared with the textarea's minLength so the browser and the route agree. */
export const MIN_PITCH_CHARS = 20;

/**
 * Contributor and partnership pitches for /contributing-to-the-blog. These are
 * not leads: no Pardot handler, no Ads conversion, and the notification says
 * so. The pitch lives in `pitch`, not `message`, on purpose: the spam filter's
 * phrase list (spam.ts) scans `message` for "guest post" and "backlinks", which
 * is exactly what a genuine contributor writes here. The honeypot, CleanTalk
 * and the rate limit still apply. The contributor's site is `siteUrl` because
 * `website` is the honeypot's legacy name (spam.ts).
 */
export const contributeSubmissionSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
  email: z.string().trim().email('Enter a valid email address').max(200),
  company: z.string().trim().max(200).optional().or(z.literal('')),
  siteUrl: z.string().trim().url('Enter a full URL, starting with https://').max(500).optional().or(z.literal('')),
  collaborationType: z.enum(COLLABORATION_TYPES),
  topic: z.string().trim().min(1, 'Add a topic or working headline').max(200),
  pitch: z
    .string()
    .trim()
    .min(MIN_PITCH_CHARS, 'Tell us a little more about the idea')
    .max(5_000),
  samples: z.string().trim().max(1_000).optional().or(z.literal('')),
  tracking: trackingSchema.optional(),
});

export type ContributeSubmission = z.infer<typeof contributeSubmissionSchema>;
